"use client";

import { useEffect, useRef } from "react";
import { updatePreferences } from "@/lib/api";
import { useI18n } from "@/lib/i18n/I18nProvider";
import { normalizeLocale } from "@/lib/i18n/locales";
import { normalizeTheme } from "@/lib/theme/storage";
import { useThemeMode } from "@/lib/theme/ThemeProvider";
import { useSession } from "@/providers/SessionProvider";

const SAVE_DELAY_MS = 400;

/** Load locale/theme from the user profile and persist changes when authenticated. */
export function SessionPreferenceSync() {
  const { session, loading } = useSession();
  const { locale, setLocale } = useI18n();
  const { theme, setTheme } = useThemeMode();
  const skipSaveRef = useRef(true);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (loading) {
      return;
    }
    if (!session?.authenticated || !session.preferences) {
      skipSaveRef.current = true;
      return;
    }
    skipSaveRef.current = true;
    setLocale(normalizeLocale(session.preferences.locale));
    setTheme(normalizeTheme(session.preferences.theme));
    const timer = setTimeout(() => {
      skipSaveRef.current = false;
    }, 0);
    return () => clearTimeout(timer);
  }, [loading, session, setLocale, setTheme]);

  useEffect(() => {
    if (loading || !session?.authenticated || skipSaveRef.current) {
      return;
    }
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }
    saveTimerRef.current = setTimeout(() => {
      void updatePreferences({ locale, theme }).catch(() => undefined);
    }, SAVE_DELAY_MS);
  }, [locale, loading, session?.authenticated, theme]);

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, []);

  return null;
}
